import React from "react";
import { Helmet } from "react-helmet-async";
import Container from "../components/layout/Container";
import PublicHero from "../components/PublicHero";
import SubNav, { veiligheidsdienstenSubNavItems } from "../components/SubNav";
import OfferCards from "../components/offers/OfferCards";

const pageUrl = "https://missionmovement.nl/voorbereiding-veiligheidsdiensten";
const heroImage = "/img/eenheden/veiligheidsdiensten.jpg";
const ogImage =
  "https://missionmovement.nl/img/eenheden/veiligheidsdiensten.jpg";

const physicalReadinessItems = [
  {
    title: "Belastbaarheid",
    text: "Onregelmatige diensten, lange shifts, tillen, dragen, sprinten en plots schakelen vragen een lichaam dat herhaalde belasting aankan.",
  },
  {
    title: "Herhaalbare output",
    text: "Een goede testdag is niet genoeg. Je moet ook na eerdere inspanning opnieuw kunnen leveren wanneer het werk daarom vraagt.",
  },
  {
    title: "Herstel & stresstolerantie",
    text: "Slaaptekort, stress en wisselende belasting beïnvloeden herstel. Duurzame inzetbaarheid vraagt dat training en herstel elkaar ondersteunen.",
  },
];

const professionalBehaviorItems = [
  {
    title: "Werken onder druk",
    text: "Ademhaling, tempo, communicatie en beheersing helpen je rustig blijven denken wanneer tijdsdruk, vermoeidheid of spanning oplopen.",
  },
  {
    title: "Teamgedrag",
    text: "Veiligheidsdiensten draaien op betrouwbaarheid. Je moet nuttig blijven, helder communiceren en verantwoordelijkheid nemen wanneer de druk stijgt.",
  },
  {
    title: "De-escalatie & controle",
    text: "Niet elke situatie wordt beter door sterker of luider te zijn. Professionele aanwezigheid en gecontroleerde fysieke inzet maken vaak het verschil.",
  },
];

const roleItems = [
  {
    title: "Politie",
    text: "Conflictsituaties, achtervolging, fixeren, de-escalatie en lange operationele dagen vragen een mix van fysieke paraatheid en controle.",
  },
  {
    title: "Brandweer",
    text: "Hitte, uitrusting, trappen, tillen, slepen, dragen en werken in onhandige posities vragen acute output en robuuste belastbaarheid.",
  },
  {
    title: "KMar",
    text: "Beveiliging, handhaving, militaire context, alertheid en professionele aanwezigheid vragen fysieke inzetbaarheid met discipline en scherpte.",
  },
];

const methodSteps = [
  {
    title: "Startniveau",
    text: "We brengen in kaart waar je nu staat: conditie, kracht, herstel, ervaring, pijnsignalen en praktische context.",
  },
  {
    title: "Functie-eisen",
    text: "De voorbereiding wordt gekoppeld aan de richting waar je naartoe werkt, niet aan een algemeen fitnessdoel.",
  },
  {
    title: "Belastbaarheid",
    text: "Je bouwt capaciteit op om fysieke en mentale belasting herhaaldelijk beter te verdragen.",
  },
  {
    title: "Progressie",
    text: "De opbouw volgt een lijn waarin training, herstel en uitvoerbaarheid samen bewaakt worden.",
  },
  {
    title: "Bijsturen",
    text: "Waar nodig wordt aangepast op basis van voortgang, klachten, herstel en de werkelijkheid van jouw week.",
  },
];

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Voorbereiding Veiligheidsdiensten",
  url: pageUrl,
  inLanguage: "nl-NL",
  description:
    "Bereid je gericht voor op politie, brandweer, KMar en veiligheidsdiensten met aandacht voor belastbaarheid, werken onder druk en duurzame inzetbaarheid.",
  publisher: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Voorbereiding Veiligheidsdiensten",
  url: pageUrl,
  provider: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
  areaServed: {
    "@type": "Country",
    name: "Netherlands",
  },
  serviceType:
    "Fysieke en mentale voorbereiding voor politie, brandweer, KMar en veiligheidsdiensten",
  description:
    "Mission Movement helpt kandidaten richting veiligheidsdiensten bouwen aan belastbaarheid, werken onder druk, teamgedrag en duurzame inzetbaarheid.",
};

const VoorbereidingVeiligheidsdiensten = () => {
  return (
    <main className="min-h-screen bg-mmPage text-mmText">
      <Helmet>
        <title>
          Voorbereiding Veiligheidsdiensten | Politie, Brandweer & KMar
        </title>
        <meta
          name="description"
          content="Bereid je gericht voor op politie, brandweer, KMar en veiligheidsdiensten met aandacht voor belastbaarheid, werken onder druk, onregelmatige diensten, teamgedrag en duurzame inzetbaarheid."
        />
        <meta
          property="og:title"
          content="Voorbereiding Veiligheidsdiensten | Mission Movement"
        />
        <meta
          property="og:description"
          content="Gerichte voorbereiding voor politie, brandweer, KMar en veiligheidsdiensten met aandacht voor belastbaarheid, werken onder druk en duurzame inzetbaarheid."
        />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <PublicHero
        eyebrow="Voorbereiding Veiligheidsdiensten"
        title="Voorbereiding Veiligheidsdiensten"
        subtitle="Gerichte voorbereiding voor wie fysiek en mentaal inzetbaar wil zijn in werk waar druk, verantwoordelijkheid en onvoorspelbaarheid samenkomen."
        imageSrc={heroImage}
        imageAlt="Voorbereiding voor veiligheidsdiensten"
        imageClassName="object-center"
        compact
      />

      <div className="relative shadow-sm">
        <SubNav
          variant="dark"
          scrollable
          align="center"
          items={veiligheidsdienstenSubNavItems}
        />
      </div>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Waarom voorbereiding meer vraagt dan algemene fitness
            </h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Kracht en conditie tellen. Maar voorbereiding op
                veiligheidsdiensten gaat verder dan algemene fitness. Politie,
                brandweer, KMar en vergelijkbare functies vragen dat je bruikbaar
                blijft wanneer verantwoordelijkheid, mensen, conflict,
                vermoeidheid en soms gevaar tegelijk aanwezig zijn.
              </p>
              <p>
                Goede voorbereiding bouwt daarom niet alleen fysieke capaciteit,
                maar ook herhaalbaarheid, communicatie en veerkracht. Je moet
                kunnen handelen zonder dat vermoeidheid direct ten koste gaat
                van je oordeel, je gedrag of je betrouwbaarheid.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl mb-10">
            <h2 className="mm-h2 mb-6">
              Belastbaarheid voor onregelmatige en fysieke inzet
            </h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Veiligheidswerk bestaat zelden uit één nette inspanning. Lange
              diensten, onregelmatige uren, plotseling fysiek werk en beperkte
              hersteltijd kunnen elkaar opvolgen. Afhankelijk van de functie
              krijgen gewrichten, rug, schouders, knieën, voeten en grip veel te
              verwerken. Daarom telt duurzame inzetbaarheid zwaarder dan alleen
              een piekmoment.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {physicalReadinessItems.map((item, index) => (
              <article key={item.title} className="mm-card p-6">
                <div className="h-9 w-9 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Werken onder druk vraagt meer dan fysieke capaciteit
            </h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Beslissingen moeten soms worden genomen terwijl je moe bent,
                spanning voelt of weinig tijd hebt. Fysieke voorbereiding moet
                rustig denken ondersteunen, niet vervangen.
              </p>
              <p>
                Inzetbaarheid blijkt in hoe je ademt, paceert, communiceert en
                beheerst blijft. Binnen veiligheidsdiensten verzwakken ego,
                paniek, klagen of chaotisch gedrag niet alleen jezelf, maar ook
                het team.
              </p>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-3 mt-10">
            {professionalBehaviorItems.map((item, index) => (
              <article key={item.title} className="mm-card p-6">
                <div className="h-9 w-9 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Politie, brandweer en KMar vragen niet hetzelfde
            </h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              De basis overlapt, maar de functie bepaalt de accenten. Goede
              voorbereiding vertrekt dus niet vanuit een willekeurig schema,
              maar vanuit wat het werk werkelijk vraagt.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3 mt-10">
            {roleItems.map((item) => (
              <article key={item.title} className="mm-card p-6">
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">
              Herstel, slaap, stress en duurzame inzetbaarheid
            </h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Een paar zware sessies maken je nog niet duurzaam inzetbaar.
                Langdurig werk in veiligheidsdiensten vraagt dat slaap,
                stressbelasting en herstel serieus worden genomen, zeker bij
                onregelmatige diensten.
              </p>
              <p>
                Blessurepreventie en belastingsturing zijn geen bijzaak. Een
                lichaam dat structureel te weinig herstelt, wordt minder
                betrouwbaar wanneer het erop aankomt. Goede voorbereiding kijkt
                dus verder dan alleen harder trainen.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <h2 className="mm-h2 mb-4">Hoe Mission Movement opbouwt</h2>
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-3xl mb-10">
            De voorbereiding start bij de persoon en de functie. Eerst wordt
            duidelijk waar je nu staat en wat jouw richting straks vraagt.
            Daarna werk je gericht aan het verschil daartussen, met bijsturing
            wanneer herstel, klachten of voortgang daar aanleiding toe geven.
          </p>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
            {methodSteps.map((step, index) => (
              <article key={step.title} className="mm-card p-5">
                <div className="h-8 w-8 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-4">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-sm mb-3">
                  {step.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {step.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mm-h2 mb-5">Start gericht richting jouw functie</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              Train met persoonlijke begeleiding op basis van je startniveau,
              functie-eisen en belastbaarheid.
            </p>
          </div>
        </Container>
        <OfferCards variant="veiligheidsdiensten" context="embedded" />
      </section>
    </main>
  );
};

export default VoorbereidingVeiligheidsdiensten;
